"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  isSafeNavPath,
  isSafeScrollY,
  stripBasePath,
  type PublisherOutboundMessage,
} from "./publisher-nav-protocol";

function currentPath(): string {
  return location.pathname + location.search + location.hash;
}

// At most one publisher:scroll per window, trailing edge included so the
// final resting position (e.g. after a fling) is always reported even though
// it arrives mid-window.
const SCROLL_THROTTLE_MS = 250;

/**
 * Client island rendered only when PUBLISHER_SANDBOX=1. Publisher (apps/publisher)
 * iframes fin-ai from a web-dev-sandboxes Sandbox and needs to know when the user
 * navigates around inside the iframe so it can keep its own URL / preview chrome
 * in sync. This component posts `publisher:nav-start` on same-origin link clicks
 * (and on `pagehide`, so a hard navigation still raises the parent's progress
 * bar), `publisher:nav` on every history push/replace/popstate, and
 * `publisher:nav-done` once a route change has actually painted, and a
 * throttled `publisher:scroll` on scroll. Clicks that would leave the editing
 * session post `publisher:page-unavailable` instead, so the parent can raise
 * its "Page unavailable" state. It also accepts
 * `publisher:navigate`/`publisher:reload`/`publisher:scroll-to` from the
 * parent so the parent can drive a soft navigation (or restore scroll
 * position) instead of remounting the iframe.
 *
 * Self-disables when the page is not iframed, so it's harmless if the env gate
 * ever accidentally lands in a non-Publisher context.
 */
export function PublisherNavBridge({
  canonicalHosts = [],
  externalPathPrefixes = [],
}: {
  /**
   * Production host(s) the previewed app is served under (e.g.
   * "www.intercom.com" / "intercom.com" for intercom-com). Only links on one of
   * these hosts are candidates for in-frame soft navigation; a link on any
   * other host is swallowed and reported as `publisher:page-unavailable`.
   */
  canonicalHosts?: string[];
  /**
   * Same-host path prefixes served by a different system than the sandbox
   * (help centre, blog, changelog, …). These stay external — swallowed and
   * reported as `publisher:page-unavailable` — even though their host is
   * canonical, because soft-navigating them would 404 the preview.
   */
  externalPathPrefixes?: string[];
} = {}) {
  const router = useRouter();

  // Held in refs so the long-lived capture listener reads the latest values
  // without the effect re-subscribing on every render (inline-array props and
  // the `[]` defaults change identity each render).
  const canonicalHostsRef = useRef(canonicalHosts);
  const externalPathPrefixesRef = useRef(externalPathPrefixes);
  canonicalHostsRef.current = canonicalHosts;
  externalPathPrefixesRef.current = externalPathPrefixes;

  useEffect(() => {
    if (window.parent === window) return;

    const post = (message: PublisherOutboundMessage) => {
      try {
        window.parent.postMessage(message, "*");
      } catch {
        // parent may reject cross-origin postMessage; nothing to do
      }
    };

    // publisher:nav fires at pushState/commit, which is optimistic on a
    // cold-compile route — a double rAF pushes nav-done past the actual paint
    // so it's a trustworthy completion signal for the parent's progress bar.
    // Scheduled off sendNav rather than a usePathname() effect because that
    // hook does not change for query-only or hash-only navigation, which would
    // strand the parent's bar until its watchdog.
    let outer = 0;
    let inner = 0;
    const scheduleNavDone = () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() =>
          post({ type: "publisher:nav-done", path: currentPath() }),
        );
      });
    };

    const sendNav = () => {
      post({ type: "publisher:nav", path: currentPath() });
      scheduleNavDone();
    };

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      if (event.button !== 0) return;
      const target = event.target as Element | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.charAt(0) === "#") return;
      try {
        const url = new URL(anchor.href, location.href);
        // mailto:/tel:/etc. have an opaque origin, so they would read as
        // off-origin below. They never navigate the frame — leave them to the
        // browser's own handler.
        if (url.protocol !== "http:" && url.protocol !== "https:") return;
        // Origin is tested before the target bail-out on purpose: footer links
        // render as target="_blank", so a target-first check would let every
        // external footer link escape this branch entirely.
        if (url.origin !== location.origin) {
          // The footer hardcodes absolute production URLs. A link on a canonical
          // host whose path this app actually serves is rewritten onto the
          // sandbox origin + signed basePath and soft-navigated, rather than
          // hard-loading production into the frame (WS5-711).
          const servedByThisApp =
            canonicalHostsRef.current.includes(url.host) &&
            !externalPathPrefixesRef.current.some(
              (prefix) =>
                url.pathname === prefix ||
                url.pathname.startsWith(`${prefix}/`),
            );
          if (servedByThisApp) {
            const basePath =
              location.pathname.match(/^\/preview\/[^/]+/)?.[0] ?? null;
            const appPath = url.pathname + url.search + url.hash;
            event.preventDefault();
            post({
              type: "publisher:nav-start",
              path: (basePath ?? "") + appPath,
            });
            router.push(appPath);
            return;
          }
          // A different host, or a same-host path served by another system.
          // Those land on a production URL that refuses to be framed, and the
          // editing session cannot serve them either — swallow the click
          // entirely and let the parent raise "Page unavailable" (WEB-2554,
          // superseding the new-tab fallback from WS5-710).
          event.preventDefault();
          post({ type: "publisher:page-unavailable", url: url.href });
          return;
        }
        // Same-origin links that ask for another browsing context are the
        // browser's business, not ours.
        if (anchor.target && anchor.target !== "" && anchor.target !== "_self")
          return;
        post({
          type: "publisher:nav-start",
          path: url.pathname + url.search + url.hash,
        });
      } catch {
        // malformed href; ignore
      }
    };
    document.addEventListener("click", onClick, true);

    const onPageHide = () =>
      post({ type: "publisher:nav-start", path: currentPath() });
    window.addEventListener("pagehide", onPageHide);

    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function (...args) {
      const result = origPush.apply(this, args);
      sendNav();
      return result;
    };
    history.replaceState = function (...args) {
      const result = origReplace.apply(this, args);
      sendNav();
      return result;
    };
    window.addEventListener("popstate", sendNav);
    // Native `href="#..."` navigation and programmatic `location.hash = "..."`
    // both bypass pushState/replaceState/popstate but do fire hashchange.
    window.addEventListener("hashchange", sendNav);

    sendNav();

    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("popstate", sendNav);
      window.removeEventListener("hashchange", sendNav);
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }, [router]);

  useEffect(() => {
    if (window.parent === window) return;

    const post = (message: PublisherOutboundMessage) => {
      try {
        window.parent.postMessage(message, "*");
      } catch {
        // parent may reject cross-origin postMessage; nothing to do
      }
    };

    let lastSentAt = 0;
    let trailingTimer: ReturnType<typeof setTimeout> | null = null;
    const sendScroll = () => {
      lastSentAt = Date.now();
      post({ type: "publisher:scroll", y: window.scrollY });
    };
    const onScroll = () => {
      const elapsed = Date.now() - lastSentAt;
      if (elapsed >= SCROLL_THROTTLE_MS) {
        if (trailingTimer) {
          clearTimeout(trailingTimer);
          trailingTimer = null;
        }
        sendScroll();
        return;
      }
      if (!trailingTimer) {
        trailingTimer = setTimeout(() => {
          trailingTimer = null;
          sendScroll();
        }, SCROLL_THROTTLE_MS - elapsed);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (trailingTimer) clearTimeout(trailingTimer);
    };
  }, []);

  useEffect(() => {
    if (window.parent === window) return;

    const ack = () => {
      try {
        window.parent.postMessage({ type: "publisher:nav-ack" }, "*");
      } catch {
        // see above
      }
    };

    let raf1 = 0;
    let raf2 = 0;

    const handler = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      const data = event.data as {
        type?: unknown;
        path?: unknown;
        y?: unknown;
      } | null;
      if (!data || typeof data !== "object") return;

      if (data.type === "publisher:reload") {
        ack();
        location.reload();
        return;
      }
      if (data.type === "publisher:navigate" && isSafeNavPath(data.path)) {
        ack();
        // The sandbox's basePath is signed per-session, so derive it from the
        // current URL rather than trusting an env var that may not be exposed
        // to the client in every app.
        const basePath =
          location.pathname.match(/^\/preview\/[^/]+/)?.[0] ?? null;
        router.push(stripBasePath(data.path, basePath));
        return;
      }
      // Deliberately not acked: nav-ack cancels the parent's pending
      // soft-navigation fallback timer, and a scroll restore lands inside that
      // window often enough to strand the navigation it belongs to.
      if (data.type === "publisher:scroll-to" && isSafeScrollY(data.y)) {
        const y = data.y;
        window.scrollTo(0, y);
        // Late layout shifts (fonts, images, cold-compile content) land after
        // this first call and clobber it, so re-apply once more after paint.
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
        raf1 = requestAnimationFrame(() => {
          raf2 = requestAnimationFrame(() => window.scrollTo(0, y));
        });
      }
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [router]);

  return null;
}
